/**
 * Created by Ning Jiang on 4/19/2016.
 */

import {AvalancheParsedGameData} from "@nolimitcity/slot-game/bin/game/reel/avalanchereel/AvalancheParsedServerData";
import {FITHServerDataParser} from "../FITHServerDataParser";
import {IDynamiteExtraValue, IFITHGameData} from "./IFITHServerData";
import {FITHGameConfig} from "../../FITHGameConfig";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";

export class FITHParsedGameData extends AvalancheParsedGameData {
    private readonly _brokeTheBank: boolean;
    private readonly _dynamiteExtraValues: IDynamiteExtraValue[];
    private readonly _activeRowsOpenedEnteringBonus: number;
    private readonly _dwarfPosition: number[];
    private readonly _bottomCollectorActivated: boolean[];
    private readonly _multiplierNextSpin: number;
    private readonly _openPositionsBonus: boolean[][];
    private readonly _waysWonTotalRound: number;
    private readonly _wildTransform: boolean;
    private readonly _wildTransformPositions: boolean[][];
    private readonly _wildTransformSymbolPositions: boolean[][];
    private readonly _bonusPositionsBeforeSpin: boolean[][];
    private readonly _bonusValuesAfterSpin: number[][];
    private readonly _bonusWinTimesBetBefore: number;
    private readonly _bonusWinTimesBetAfter: number;
    private readonly _bonusNewPositions: boolean[][];
    private readonly _originalReels: string[][];
    private readonly _totalNumberOfWays: string;
    private readonly _explodePositions: boolean[][];
    private readonly _baseActiveRowsBefore: number;
    private readonly _baseActiveRowsAfter: number;
    private readonly _avalancheTotalWin: number;
    private readonly _currentAvalancheWinRatio: number;
    private readonly _currentAvalancheWinRatioConfig: IWinRatioConfig | null;
    // bigWin
    private readonly _currentBigWinWinRatio: number;
    private _isEndBigWin: boolean;

    // #354
    private readonly _secondDwarfPos: number[];

    constructor(rawData: IFITHGameData, dataParser: FITHServerDataParser) {
        const originalReels: string[][] = JSON.parse(JSON.stringify(rawData.reels));

        rawData.reels = dataParser.parseReelData(rawData);
        rawData.reelsAfterCollapse = dataParser.parseReelsAfterCollapseData(rawData);

        super(rawData, dataParser);

        this._brokeTheBank = dataParser.parseBrokeTheBank(rawData);
        this._dynamiteExtraValues = dataParser.parseDynamiteExtraValues(rawData);
        this._activeRowsOpenedEnteringBonus = dataParser.parseActiveRowsOpenedEnteringBonus(rawData);
        this._dwarfPosition = dataParser.parseDwarfPosition(rawData);
        this._bottomCollectorActivated = dataParser.parseBottomCollectorActivated(rawData);
        this._multiplierNextSpin = dataParser.parseMultiplierNextSpin(rawData);
        this._openPositionsBonus = dataParser.parseOpenPositionsBonus(rawData);
        this._waysWonTotalRound = dataParser.parseWaysWonTotalRound(rawData);
        this._wildTransform = dataParser.parseWildTransform(rawData);
        this._wildTransformPositions = dataParser.parseWildTransformPositions(rawData);
        this._wildTransformSymbolPositions = dataParser.parseWildTransformSymbolPositions(rawData);
        this._bonusPositionsBeforeSpin = dataParser.parseBonusPositionsBeforeSpin(rawData);
        this._bonusValuesAfterSpin = dataParser.parseBonusValuesAfterSpin(rawData);
        this._bonusWinTimesBetBefore = dataParser.parseBonusWinTimesBetBefore(rawData);
        this._bonusWinTimesBetAfter = dataParser.parseBonusWinTimesBetAfter(rawData);
        this._bonusNewPositions = dataParser.parseBonusNewPositions(rawData);
        this._originalReels = originalReels;
        this._totalNumberOfWays = dataParser.parseTotalNumberOfWays(rawData);
        this._explodePositions = dataParser.parseExplodePositions(rawData);
        this._baseActiveRowsBefore = dataParser.parseBaseActiveRowsBefore(rawData);
        this._baseActiveRowsAfter = dataParser.parseBaseActiveRowsAfter(rawData);
        this._avalancheTotalWin = dataParser.parseAvalancheTotalWin(rawData);

        // bigWin
        this._currentBigWinWinRatio = this.totalWin / Number(rawData.playedBetValue);
        this._isEndBigWin = this._currentBigWinWinRatio >= FITHGameConfig.instance.WIN_RATIOS.bigWin[0].ratio;
        this._currentAvalancheWinRatio = this._avalancheTotalWin / Number(rawData.playedBetValue);
        this._currentAvalancheWinRatioConfig = FITHParsedGameData.getAvalancheWinRatioConfig(this._currentAvalancheWinRatio);

        this._secondDwarfPos = dataParser.parseSecondDwarfPos(rawData, originalReels);
    }

    get brokeTheBank(): boolean {
        return this._brokeTheBank;
    }

    get dynamiteExtraValues(): IDynamiteExtraValue[] {
        return this._dynamiteExtraValues;
    }

    get activeRowsOpenedEnteringBonus(): number {
        return this._activeRowsOpenedEnteringBonus;
    }

    get dwarfPosition(): number[] {
        return this._dwarfPosition;
    }

    get bottomCollectorActivated(): boolean[] {
        return this._bottomCollectorActivated;
    }

    get multiplierNextSpin(): number {
        return this._multiplierNextSpin;
    }

    get openPositionsBonus(): boolean[][] {
        return this._openPositionsBonus;
    }

    get waysWonTotalRound(): number {
        return this._waysWonTotalRound;
    }

    get wildTransformPositions(): boolean[][] {
        return this._wildTransformPositions;
    }

    get wildTransform(): boolean {
        return this._wildTransform;
    }

    get wildTransformSymbolPositions(): boolean[][] {
        return this._wildTransformSymbolPositions;
    }

    get bonusPositionsBeforeSpin(): boolean[][] {
        return this._bonusPositionsBeforeSpin;
    }

    get bonusValuesAfterSpin(): number[][] {
        return this._bonusValuesAfterSpin;
    }

    get bonusWinTimesBetBefore(): number {
        return this._bonusWinTimesBetBefore;
    }

    get bonusWinTimesBetAfter(): number {
        return this._bonusWinTimesBetAfter;
    }

    get bonusNewPositions(): boolean[][] {
        return this._bonusNewPositions;
    }

    get originalReels(): string[][] {
        return this._originalReels;
    }

    get totalNumberOfWays(): string {
        return this._totalNumberOfWays;
    }

    get explodePositions(): boolean[][] {
        return this._explodePositions;
    }

    get baseActiveRowsBefore(): number {
        return this._baseActiveRowsBefore;
    }

    get baseActiveRowsAfter(): number {
        return this._baseActiveRowsAfter;
    }

    public get avalancheTotalWin(): number {
        return this._avalancheTotalWin;
    }

    public get currentBigWinWinRatio(): number {
        return this._currentBigWinWinRatio;
    }

    public get isEndBigWin(): boolean {
        return this._isEndBigWin;
    }

    public set isEndBigWin(value: boolean) {
        this._isEndBigWin = value;
    }

    public get currentAvalancheWinRatioConfig(): IWinRatioConfig | null {
        return this._currentAvalancheWinRatioConfig;
    }

    get secondDwarfPos(): number[] {
        return this._secondDwarfPos;
    }

    private static getAvalancheWinRatioConfig(winRatio: number): IWinRatioConfig | null {
        let winLevels: IWinRatioConfig[] = FITHGameConfig.instance.WIN_RATIOS.normalWin.concat();
        winLevels = winLevels.concat(FITHGameConfig.instance.WIN_RATIOS.bigWin);

        for (let i: number = winLevels.length - 1; i >= 0; i--) {
            const winLevel = winLevels[i];
            if (winRatio >= winLevel.ratio) {
                return winLevel;
            }
        }
        return null;
    }
}